import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Trans, Translation, withTranslation } from 'react-i18next';
import { titleize } from 'underscore.string';
import _ from 'underscore';
import { DropdownButton, MenuItem } from 'react-bootstrap';
import { showToast } from 'stores/ToastStore';
import CargomaticSwitch from 'components/v2/Switch';
import { AuthorizationWrapper } from 'components/wrappers/AuthorizationWrapper';
import { REGIONS } from 'constants/Regions';
import {
  isDrayageShipmentNotCanceled,
  isAssignedCarrier,
} from 'helpers/shipments';
import InfoIcon from 'components/v2/icons/InfoIcon';
import formatMoney from 'utils/formatters/Money';
import InvoiceGroupingModal from 'components/modules/orders/accounting/InvoiceGroupingModal';
import { CONFIGURED_FEATURES, featureEnabled } from 'helpers/feature-flags';
import CargomaticModal from 'components/v2/modal';
import ShipmentCharges from './ShipmentCharges';

const USER_TYPES = {
  SHIPPER: 'shipper',
  CARRIER: 'carrier',
};

class GenerateLedgerModal extends Component {
  static propTypes = {
    show: PropTypes.bool,
    ledgerType: PropTypes.oneOf(['receivables', 'payables']).isRequired,
    onClose: PropTypes.func.isRequired,
    onGenerateLedger: PropTypes.func.isRequired,
    shipments: PropTypes.array,
    shipper: PropTypes.object,
    carrier: PropTypes.object,
    shipperInvoiceSettings: PropTypes.object,
  };

  constructor(props) {
    super(props);
    const {
      equipment,
      ledgerType,
      shipments,
      shipper,
      carrier,
      orderId: _orderId,
    } = props;

    const wantsMultipleInvoiceDocs =
      (shipper.invoice_document_preferences || []).filter(
        (pref) => pref.slug !== 'shipment_invoice' && pref.slug !== 'invoice',
      ).length > 0;

    const disableSelectAll =
      props.ledgerType === 'receivables' &&
      wantsMultipleInvoiceDocs &&
      shipments.length > 1;

    const masterShipmentChargesMap =
      shipments.length > 0
        ? mapShipmentCharges(
            shipments || [],
            equipment,
            ledgerType,
            disableSelectAll,
          )
        : {};

    if (disableSelectAll) {
      const keys = Object.keys(masterShipmentChargesMap);
      keys.forEach((el) => {
        masterShipmentChargesMap[el].selectAllDisabled = false;
      });
    }

    const chargeCountsByShipment = getShipmentChargesCount(
      shipments,
      ledgerType,
    );
    const allChargeTypes = getAllGroupableChargeTypes(
      shipments,
      ledgerType,
      chargeCountsByShipment,
    );

    const user = ledgerType === 'receivables' ? shipper : carrier;
    const userType =
      ledgerType === 'receivables' ? USER_TYPES.SHIPPER : USER_TYPES.CARRIER;
    const groupLikeLineItemSetting = !!getUserLineItemGroupingSetting(
      user,
      userType,
    );

    const shipmentChargesMap = getDefaultShipmentChargeMap(
      masterShipmentChargesMap,
      allChargeTypes,
      groupLikeLineItemSetting,
    );

    const generateDisabled = !chargesSelected(
      masterShipmentChargesMap,
      disableSelectAll,
    );
    const overallTotal = calculateOverallTotal(masterShipmentChargesMap);

    this.state = {
      ledgerDate: moment(),
      masterShipmentChargesMap,
      shipmentChargesMap,
      generateDisabled,
      overallTotal,
      allChargeTypes,
      chargeCountsByShipment,
      individualGroupedTypes: [],
      groupAllLikeItems: groupLikeLineItemSetting,
      disableSelectAll,
    };
  }

  getTitle() {
    return this.props.ledgerType === 'receivables' ? 'Invoice' : 'Payout';
  }

  shipperHasEnabledEDI = () => {
    const { shipper } = this.props;
    if (shipper) {
      const { customer_configurations } = shipper;
      return (
        customer_configurations &&
        customer_configurations.enabled_events &&
        customer_configurations.enabled_events.invoice_created
      );
    }

    return false;
  };

  transformPayload() {
    const { shipmentChargesMap } = this.state;

    return Object.keys(shipmentChargesMap).reduce(
      (selectedCharges, shipmentId) => {
        const charges = shipmentChargesMap[shipmentId];
        const groupedChargeTypes = charges.lineItems
          .filter((c) => c.grouped)
          .map((c) => c.type);

        const selected = charges.selected;
        if (selected.length > 0) {
          return {
            ...selectedCharges,
            [shipmentId]: { selected, groupedChargeTypes },
          };
        }
        return selectedCharges;
      },
      {},
    );
  }
  handleEDISend = () => this.handleGenerateLedger(true);

  handleGenerateLedger = (sendEdi = false, sendEmail = false) => {
    const { ledgerDate } = this.state;
    this.props.onGenerateLedger(
      ledgerDate,
      this.transformPayload(),
      sendEdi,
      sendEmail,
    );
  };

  handleClose = () => this.props.onClose();

  handleToggleSelectAll = (eventKey) => {
    const checked = eventKey === 'all';
    this.setState((prevState) => {
      const { shipmentChargesMap: currentShipmentCharges } = prevState;

      const shipmentChargesMap = Object.values(currentShipmentCharges).reduce(
        (runningMap, charges) => {
          let selected;
          let total;
          let selectAllChecked = false;
          if (checked) {
            const { lineItems, chargeables } = charges;
            selected = [...chargeables];
            total = getTotalForShipment(lineItems, chargeables);
            selectAllChecked = chargeables.length > 0;
          } else {
            selected = [];
            total = 0;
          }

          return {
            ...runningMap,
            [charges._id]: {
              ...charges,
              selected,
              selectAllChecked,
              total,
            },
          };
        },
        {},
      );

      let generateDisabled;
      let overallTotal;

      if (checked) {
        generateDisabled = !chargesSelected(shipmentChargesMap);
        overallTotal = calculateOverallTotal(shipmentChargesMap);
      } else {
        generateDisabled = true;
        overallTotal = 0;
      }

      return {
        shipmentChargesMap,
        generateDisabled,
        overallTotal,
      };
    });
  };

  handleToggleSelectAllForShipment = (shipmentId, checked) => {
    if (this.handleInvoiceDocPrefCheck(shipmentId, checked)) return;
    this.setState((prevState) => {
      const { shipmentChargesMap: currentShipmentCharges } = prevState;

      const charges = { ...currentShipmentCharges[shipmentId] };

      let selected;
      let total;
      let selectAllChecked = false;
      if (checked) {
        const { lineItems, chargeables } = charges;
        selected = [...chargeables];
        total = getTotalForShipment(lineItems, chargeables);
        selectAllChecked = chargeables.length > 0;
      } else {
        selected = [];
        total = 0;
      }

      charges.selected = selected;
      charges.selectAllChecked = selectAllChecked;
      charges.total = total;

      const shipmentChargesMap = {
        ...currentShipmentCharges,
        [shipmentId]: charges,
      };
      const generateDisabled = !chargesSelected(shipmentChargesMap);
      const overallTotal = calculateOverallTotal(shipmentChargesMap);

      return {
        shipmentChargesMap,
        generateDisabled,
        overallTotal,
      };
    });
  };

  handleToggleSelectCharge = (shipmentId, chargeId, checked) => {
    if (this.handleInvoiceDocPrefCheck(shipmentId, checked)) return;
    this.setState((prevState) => {
      const { shipmentChargesMap: currentShipmentCharges } = prevState;

      let selected;
      const charges = { ...currentShipmentCharges[shipmentId] };
      const { chargeables } = charges;
      if (checked) {
        selected = [...charges.selected, chargeId];
      } else {
        selected = charges.selected.filter((c) => c !== chargeId);
      }

      const selectAllChecked = selected.length === chargeables.length;

      charges.selected = selected;
      charges.selectAllChecked = selectAllChecked;
      charges.total = getTotalForShipment(charges.lineItems, selected);

      const shipmentChargesMap = {
        ...currentShipmentCharges,
        [shipmentId]: charges,
      };
      const generateDisabled = !chargesSelected(shipmentChargesMap);
      const overallTotal = calculateOverallTotal(shipmentChargesMap);

      return {
        shipmentChargesMap,
        generateDisabled,
        overallTotal,
      };
    });
  };

  handleGroupAllCharges = (groupAllLikeItems) => {
    const {
      allChargeTypes,
      individualGroupedTypes,
      masterShipmentChargesMap,
      shipmentChargesMap: currentCharges,
    } = this.state;
    const types = groupAllLikeItems ? allChargeTypes : individualGroupedTypes;
    const shipmentChargesMap = groupCharges(
      masterShipmentChargesMap,
      currentCharges,
      types,
    );

    this.setState({ groupAllLikeItems, shipmentChargesMap });
  };

  handleGroupIndividualCharge = (groupCharge, charge) => {
    const {
      masterShipmentChargesMap,
      shipmentChargesMap: currentCharges,
      individualGroupedTypes: currentIndividualGroupedTypes,
      allChargeTypes,
    } = this.state;
    const { type } = charge;
    const individualGroupedTypes = groupCharge
      ? _.uniq([...currentIndividualGroupedTypes, type])
      : currentIndividualGroupedTypes.filter((c) => c !== type);
    const shipmentChargesMap = groupCharges(
      masterShipmentChargesMap,
      currentCharges,
      individualGroupedTypes,
    );
    const groupAllLikeItems =
      individualGroupedTypes.length === allChargeTypes.length;

    this.setState({
      shipmentChargesMap,
      individualGroupedTypes,
      groupAllLikeItems,
    });
  };

  handleInvoiceDocPrefCheck = (shipmentId, checked) => {
    const transformedPayload = this.transformPayload();
    if (
      checked &&
      this.state.disableSelectAll &&
      Object.keys(transformedPayload).filter((el) => el !== shipmentId).length >
        0
    ) {
      this.enqueueToast();
      return true;
    }
    return false;
  };

  enqueueToast = _.debounce(
    function () {
      showToast(
        <Translation>{(t) => t('MULTI_SELECT_DISABLED')}</Translation>,
        <Translation>{(t) => t('YOU_CAN_SELECT_ONLY_ONE_JOB')}</Translation>,
        'error',
      );
    },
    500,
    true,
  );

  renderShipments() {
    const { t } = this.props;
    const { shipmentChargesMap, chargeCountsByShipment, disableSelectAll } =
      this.state;
    return (
      <div
        className="p-4 -mx-3 -my-4 overflow-x-hidden overflow-y-auto bg-grey-lightest"
        style={{ maxHeight: '80vh' }}
      >
        <div className="flex justify-between mb-4">
          <div>
            {disableSelectAll && (
              <div className="flex">
                <span className="mr-1">
                  <InfoIcon />
                </span>
                <div className="block mb-4 text-xs italic overflow-auto">
                  {t('SHIPPER_HAS_INVOICE_DOC_PREFERENCES')}
                </div>
              </div>
            )}
            <div className="block mb-4">
              {this.getTitle() === 'Invoice' ? (
                <Trans
                  i18nKey="SELECT_CHARGES_FOR_INVOICE"
                  components={[<span key="bold" className="font-bold" />]}
                />
              ) : (
                <Trans
                  i18nKey="SELECT_CHARGES_FOR_PAYOUT"
                  components={[<span key="bold" className="font-bold" />]}
                />
              )}
            </div>
          </div>
          <div className="flex flex-col">
            <div className="self-end">
              <DropdownButton
                title={t('SELECT')}
                onSelect={this.handleToggleSelectAll}
              >
                <MenuItem disabled={disableSelectAll} eventKey="all">
                  {titleize(t('ALL'))}
                </MenuItem>
                <MenuItem eventKey="none">{t('NONE')}</MenuItem>
              </DropdownButton>
            </div>
            <div className="flex my-2">
              <p className="my-auto pr-2">{t('GROUP_LIKE_ITEMS')}</p>
              <CargomaticSwitch
                className="my-auto"
                height={16}
                width={32}
                uncheckedIcon={false}
                checkedIcon={false}
                checked={this.state.groupAllLikeItems}
                onChange={this.handleGroupAllCharges}
              />
            </div>
          </div>
        </div>
        {Object.keys(shipmentChargesMap).map((shipmentId) => {
          const charges = shipmentChargesMap[shipmentId];
          const chargeCountMap = chargeCountsByShipment[shipmentId];
          return (
            <ShipmentCharges
              key={shipmentId}
              charges={charges}
              chargeCountMap={chargeCountMap}
              onToggleSelect={this.handleToggleSelectCharge}
              onToggleSelectAll={this.handleToggleSelectAllForShipment}
              onGroupItem={this.handleGroupIndividualCharge}
            />
          );
        })}
      </div>
    );
  }

  renderGenerateInvoiceLedgerButton() {
    const { t } = this.props;
    const title = this.getTitle();

    return (
      <div>
        <AuthorizationWrapper excludedRegions={[REGIONS.AP]}>
          <button
            className={`button button-green accounting-generateLedgerModal-create${title}`}
            disabled={this.state.generateDisabled}
            onClick={() => this.handleGenerateLedger(false, true)}
          >
            {t('GENERATE_AND_SEND_EMAIL_INVOICE')}
          </button>
        </AuthorizationWrapper>
        <button
          className={`mx-2 button button-green accounting-generateLedgerModal-create${title}`}
          disabled={this.state.generateDisabled}
          onClick={() => this.handleGenerateLedger(false)}
        >
          {t('GENERATE_INVOICE')}
        </button>
      </div>
    );
  }

  renderGeneratePayoutLedgerButton() {
    const { t } = this.props;
    const title = this.getTitle();
    const sendPayoutEmail = REGION_CODE === REGIONS.US;
    return (
      <button
        className={`mx-2 button button-green accounting-generateLedgerModal-create${title}`}
        disabled={this.state.generateDisabled}
        onClick={() => this.handleGenerateLedger(false, sendPayoutEmail)}
      >
        {t('GENERATE_PAYOUT')}
      </button>
    );
  }

  renderInvoiceModalFooter(isInvoice) {
    const { t } = this.props;
    return (
      <div className="flex items-center justify-between">
        <h4 className="my-0 mr-4 text-2xl text-grey-darker">
          {formatMoney(
            this.state.overallTotal,
            this.props.shipments[0]?.currency,
          )}
        </h4>
        <div className="flex items-center justify-end">
          {isInvoice
            ? this.renderGenerateInvoiceLedgerButton()
            : this.renderGeneratePayoutLedgerButton()}
          {this.shipperHasEnabledEDI() &&
            this.props.ledgerType !== 'payables' && (
              <AuthorizationWrapper excludedRegions={[REGIONS.AP]}>
                <button
                  disabled={this.state.generateDisabled}
                  className="mx-2 button button-hollow-green accounting-generateLedgerModal-createsendedi"
                  onClick={this.handleEDISend}
                >
                  {t('GENERATE_AND_SEND_EDI')}
                </button>
              </AuthorizationWrapper>
            )}
          <button
            className="bg-white button text-grey hover:text-grey-dark"
            onClick={this.handleClose}
          >
            {titleize(t('CANCEL'))}
          </button>
        </div>
      </div>
    );
  }

  render() {
    const { t } = this.props;
    const title = this.getTitle();
    const isInvoice = title === 'Invoice';
    const modaltitle = isInvoice ? t('GENERATE_INVOICE') : t('GENERATE_PAYOUT');
    if (featureEnabled(CONFIGURED_FEATURES.SSR) && isInvoice) {
      return (
        <InvoiceGroupingModal
          t={this.props.t}
          show={this.props.show}
          orderId={this.props.orderId}
          equipment={this.props.equipment}
          shipper={this.props.shipper}
          carrier={this.props.carrier}
          shipments={this.props.shipments}
          ledgerType={this.props.ledgerType}
          onClose={this.props.onClose}
          useShipmentAddress={this.props.useShipmentAddress}
          invoiceSettings={this.props.shipperInvoiceSettings}
        />
      );
    }
    return (
      <CargomaticModal
        title={modaltitle}
        dialogClassName={isInvoice ? 'w-3/5' : ''}
        show={this.props.show}
        onHide={this.handleClose}
        body={this.renderShipments()}
        footer={this.renderInvoiceModalFooter(isInvoice)}
      />
    );
  }
}

function getUnprocessedItems(shipment, ledgerType) {
  return shipment[ledgerType].filter(
    (charge) => !charge.processed && charge.equipment,
  );
}

function getTotalForShipment(lineItems, selected = []) {
  let selectedItems;
  if (selected) {
    selectedItems = lineItems.filter((l) => selected.includes(l._id));
  } else {
    selectedItems = lineItems;
  }
  return selectedItems.reduce((total, lineItem) => total + lineItem.value, 0);
}

function calculateShipmentCharges(
  shipment,
  equipment,
  ledgerType,
  disableSelectAll,
) {
  const deliverStop = _.findWhere(shipment.stops, {
    action: 'delivery',
    empty: false,
  });

  const lineItems = getUnprocessedItems(shipment, ledgerType)
    .map((charge) => {
      const lineItemEquipments = equipment.find(
        (eq) => eq._id === charge.equipment,
      );
      const lineItemEquipment = lineItemEquipments
        ? lineItemEquipments.equipment_id
        : null;
      const data = {
        ...charge,
        shipmentId: shipment._id,
        grouped: false,
      };
      if (lineItemEquipment) {
        data.equipment = lineItemEquipment;
      } else {
        delete data.equipment;
        data.orphaned = true;
      }

      if (
        charge.category === 'base_cost' ||
        (charge.category === 'accessorials' &&
          charge.type === 'customer_adjustment')
      ) {
        data.documents = shipment.documents;
        if (deliverStop) {
          data.documents.concat(deliverStop.documents);
        }
      }
      return data;
    })

    // Exclude pending FSC charges
    .filter(
      (lineItem) =>
        lineItem.charge_code !== 'FSC' ||
        (lineItem.notes !== 'Pending' && !lineItem.has_pending_state),
    );

  const chargeables = lineItems
    .filter((l) => l.orphaned !== true)
    .map((l) => l._id);
  const selected = disableSelectAll ? [] : [...chargeables];
  const selectAllDisabled = selected.length === 0;
  const selectAllChecked = !selectAllDisabled;
  const total = selectAllDisabled
    ? 0
    : getTotalForShipment(lineItems, selected);
  const hasLineItems = lineItems.length > 0;

  return {
    _id: shipment._id,
    shipment_id: shipment.shipment_id,
    lineItems,
    chargeables,
    selected,
    selectAllChecked,
    selectAllDisabled,
    hasLineItems,
    total,
    currency: shipment?.currency,
  };
}

function isIntermodalAndHasCarrierIfPayout(shipment, ledgerType) {
  if (ledgerType === 'payables') {
    return (
      isAssignedCarrier(shipment) && isDrayageShipmentNotCanceled(shipment)
    );
  }
  return isDrayageShipmentNotCanceled(shipment);
}

function mapShipmentCharges(
  shipments,
  equipment,
  ledgerType,
  disableSelectAll = false,
) {
  return shipments
    .filter((shipment) => {
      return isIntermodalAndHasCarrierIfPayout(shipment, ledgerType);
    })
    .reduce((runningMap, shipment) => {
      const charges = calculateShipmentCharges(
        shipment,
        equipment,
        ledgerType,
        disableSelectAll,
      );

      return {
        ...runningMap,
        [shipment._id]: charges,
      };
    }, {});
}

function chargesSelected(shipmentChargesMap, disableSelectAll = false) {
  if (disableSelectAll) return false;
  return Object.values(shipmentChargesMap).some((c) => c.selected.length > 0);
}

function calculateOverallTotal(shipmentChargesMap) {
  return Object.values(shipmentChargesMap).reduce(
    (runningTotal, charges) => runningTotal + charges.total,
    0,
  );
}

function groupCharges(chargeMap, currentCharges, groupedChargeTypes) {
  const charges = Object.keys(chargeMap).reduce((groupMap, shipmentId) => {
    if (!groupMap[shipmentId]) {
      groupMap[shipmentId] = {};
    }
    const shipment = chargeMap[shipmentId];
    const chargesGroupedByEquipment = _.groupBy(
      shipment.lineItems,
      (charge) => charge.equipment,
    );
    groupMap[shipmentId] = Object.keys(chargesGroupedByEquipment).reduce(
      (groupCharges, equipment) => {
        const equipmentCharges = chargesGroupedByEquipment[equipment];
        const ungroupedCharges = equipmentCharges.filter(
          (charge) =>
            !groupedChargeTypes.includes(charge.type) ||
            !currentCharges[shipmentId].selected.includes(charge._id),
        );
        const chargesToGroup = equipmentCharges.filter(
          (charge) =>
            currentCharges[shipmentId].selected.includes(charge._id) &&
            groupedChargeTypes.includes(charge.type),
        );

        const groupedCharges = chargesToGroup.reduce(
          (chargeTypeMap, charge) => {
            const { type, value, unit_qty } = charge;
            if (!chargeTypeMap[type]) {
              chargeTypeMap[type] = {
                ...charge,
                groupedIds: [charge._id],
                grouped: true,
              };
              return chargeTypeMap;
            }
            chargeTypeMap[type].value += value;
            chargeTypeMap[type].unit_qty += unit_qty || 1;
            chargeTypeMap[type].groupedIds.push(charge._id);
            return chargeTypeMap;
          },
          {},
        );
        groupCharges = [
          ...groupCharges,
          ...ungroupedCharges,
          ...Object.values(groupedCharges),
        ];

        return groupCharges;
      },
      [],
    );

    return groupMap;
  }, {});

  return Object.keys(chargeMap).reduce((shipmentChargesMap, shipmentId) => {
    const { selected, selectAllChecked, selectAllDisabled, total, lineItems } =
      currentCharges[shipmentId];
    shipmentChargesMap[shipmentId] = {
      ...chargeMap[shipmentId],
      selectAllChecked,
      selectAllDisabled,
      total,
      lineItems: selected && selected.length ? charges[shipmentId] : lineItems,
      selected: selected && selected.length ? selected : [],
    };
    return shipmentChargesMap;
  }, {});
}

function getAllGroupableChargeTypes(shipments, ledgerType, chargeCountMap) {
  const chargeTypes = shipments.reduce((types, shipment) => {
    const shipmentChargeCount = chargeCountMap[shipment._id];
    shipment[ledgerType].forEach((ledger) => {
      const { type } = ledger;
      if (shipmentChargeCount[type] > 1) {
        types.push(type);
      }
    });
    return types;
  }, []);
  return _.uniq(chargeTypes);
}

function getShipmentChargesCount(shipments, ledgerType) {
  return shipments.reduce((chargeMap, shipment) => {
    const { _id: shipmentId } = shipment;
    chargeMap[shipmentId] = {};
    shipment[ledgerType].forEach((ledger) => {
      const { type } = ledger;
      if (!chargeMap[shipmentId][type]) {
        chargeMap[shipmentId][type] = 1;
      } else {
        chargeMap[shipmentId][type] += 1;
      }
    });

    return chargeMap;
  }, {});
}

function getDefaultShipmentChargeMap(chargeMap, allChargeTypes, groupAll) {
  if (groupAll) {
    return groupCharges(chargeMap, chargeMap, allChargeTypes);
  }
  return chargeMap;
}

function getUserLineItemGroupingSetting(user, userType) {
  if (!user) {
    return false;
  }

  if (userType === USER_TYPES.SHIPPER) {
    return user.group_like_invoice_items;
  }
  return user.group_like_payout_items;
}

export default withTranslation()(GenerateLedgerModal);
